import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  FileSpreadsheet, Download, Calendar, IndianRupee, Receipt, 
  TrendingUp, Filter, Loader2, FileText, CheckCircle2, Clock
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Accounting = () => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);
  const [invoices, setInvoices] = useState([]);
  
  // Date range state
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [billingType, setBillingType] = useState('all');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [activeTab, setActiveTab] = useState('summary');

  useEffect(() => {
    if (activeTab === 'summary') {
      fetchSummary();
    } else if (activeTab === 'monthly') {
      fetchMonthlySummary();
    } else if (activeTab === 'invoices') {
      fetchInvoices();
    }
  }, [startDate, endDate, billingType, selectedYear, activeTab]);

  const fetchSummary = async () => {
    setLoading(true);
    const token = localStorage.getItem('garage_token');
    
    try {
      const response = await fetch(
        `${API_URL}/api/garage/accounting/summary?start_date=${startDate}&end_date=${endDate}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await response.json();
      
      if (response.ok) {
        setSummary(data);
      } else {
        toast.error(data.detail || 'Failed to load summary');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const fetchMonthlySummary = async () => {
    setLoading(true);
    const token = localStorage.getItem('garage_token');
    
    try {
      const response = await fetch(
        `${API_URL}/api/garage/accounting/monthly-summary?year=${selectedYear}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await response.json();
      
      if (response.ok) {
        setMonthlyData(data);
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoices = async () => {
    setLoading(true);
    const token = localStorage.getItem('garage_token');
    
    try {
      let url = `${API_URL}/api/garage/accounting/invoices?start_date=${startDate}&end_date=${endDate}`;
      if (billingType !== 'all') {
        url += `&billing_type=${billingType}`;
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (response.ok) {
        setInvoices(data.invoices || []);
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    setDownloading(true);
    const token = localStorage.getItem('garage_token');
    
    try {
      let url = `${API_URL}/api/garage/accounting/export/excel?start_date=${startDate}&end_date=${endDate}`;
      if (billingType !== 'all') {
        url += `&billing_type=${billingType}`;
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `invoices_${startDate}_to_${endDate}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        a.remove();
        toast.success('Excel file downloaded');
      } else {
        toast.error('Failed to download');
      }
    } catch (error) {
      toast.error('Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const setQuickRange = (range) => {
    const today = new Date();
    let start, end;
    
    switch (range) {
      case 'today':
        start = end = today;
        break;
      case 'week':
        start = new Date(today);
        start.setDate(today.getDate() - 7);
        end = today;
        break;
      case 'month':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = today;
        break;
      case 'quarter':
        const quarter = Math.floor(today.getMonth() / 3);
        start = new Date(today.getFullYear(), quarter * 3, 1);
        end = today;
        break;
      case 'year':
        start = new Date(today.getFullYear(), 0, 1);
        end = today;
        break;
      default:
        return;
    }
    
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  };

  return (
    <div className="space-y-6" data-testid="accounting-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className={`text-2xl font-bold ${theme.textPrimary}`}>Accounting</h1>
          <p className={`text-sm ${theme.textSecondary}`}>Invoice summaries and financial reports</p>
        </div>
        <Button 
          onClick={handleExportExcel}
          disabled={downloading}
          className="bg-green-600 hover:bg-green-700"
        >
          {downloading ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <Download className="w-4 h-4 mr-2" />
          )}
          Export to Excel
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-700 pb-2">
        {[
          { id: 'summary', label: 'Summary', icon: TrendingUp },
          { id: 'monthly', label: 'Monthly View', icon: Calendar },
          { id: 'invoices', label: 'All Invoices', icon: Receipt }
        ].map((tab) => (
          <Button
            key={tab.id}
            variant={activeTab === tab.id ? 'default' : 'ghost'}
            onClick={() => setActiveTab(tab.id)}
            className={activeTab === tab.id ? 'bg-orange-500 hover:bg-orange-600' : ''}
          >
            <tab.icon className="w-4 h-4 mr-2" />
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Date Range Filters */}
      {activeTab !== 'monthly' && (
        <Card className={`${theme.cardBg} ${theme.border}`}>
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Calendar className={`w-4 h-4 ${theme.textSecondary}`} />
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className={`px-3 py-2 rounded ${theme.inputBg} ${theme.border} ${theme.textPrimary}`}
                />
                <span className={theme.textSecondary}>to</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className={`px-3 py-2 rounded ${theme.inputBg} ${theme.border} ${theme.textPrimary}`}
                />
              </div>
              
              <div className="flex items-center gap-2">
                <Filter className={`w-4 h-4 ${theme.textSecondary}`} />
                <Select value={billingType} onValueChange={setBillingType}>
                  <SelectTrigger className={`w-36 ${theme.inputBg} ${theme.border}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={`${theme.cardBgSolid} ${theme.border}`}>
                    <SelectItem value="all">All Invoices</SelectItem>
                    <SelectItem value="gst">GST Only</SelectItem>
                    <SelectItem value="non_gst">Non-GST Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex gap-1 ml-auto">
                {['today', 'week', 'month', 'quarter', 'year'].map((range) => (
                  <Button
                    key={range}
                    variant="outline"
                    size="sm"
                    onClick={() => setQuickRange(range)}
                    className={`${theme.border} text-xs capitalize`}
                  >
                    {range}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Year Selector for Monthly View */}
      {activeTab === 'monthly' && (
        <Card className={`${theme.cardBg} ${theme.border}`}>
          <CardContent className="py-4">
            <div className="flex items-center gap-4">
              <Calendar className={`w-4 h-4 ${theme.textSecondary}`} />
              <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                <SelectTrigger className={`w-32 ${theme.inputBg} ${theme.border}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={`${theme.cardBgSolid} ${theme.border}`}>
                  {[2024, 2025, 2026].map((year) => (
                    <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-orange-400" />
        </div>
      ) : (
        <>
          {/* Summary Tab */}
          {activeTab === 'summary' && summary && (
            <div className="space-y-6">
              {/* Overview Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className={`${theme.cardBg} ${theme.border}`}>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                        <Receipt className="w-6 h-6 text-blue-400" />
                      </div>
                      <div>
                        <p className={`text-2xl font-bold ${theme.textPrimary}`}>{summary.summary.total_invoices}</p>
                        <p className={`text-xs ${theme.textSecondary}`}>Total Invoices</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className={`${theme.cardBg} ${theme.border}`}>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                        <IndianRupee className="w-6 h-6 text-green-400" />
                      </div>
                      <div>
                        <p className={`text-2xl font-bold ${theme.textPrimary}`}>{formatCurrency(summary.summary.total_amount)}</p>
                        <p className={`text-xs ${theme.textSecondary}`}>Total Revenue</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className={`${theme.cardBg} ${theme.border}`}>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
                        <FileText className="w-6 h-6 text-amber-400" />
                      </div>
                      <div>
                        <p className={`text-2xl font-bold ${theme.textPrimary}`}>{formatCurrency(summary.summary.total_gst)}</p>
                        <p className={`text-xs ${theme.textSecondary}`}>GST Collected</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className={`${theme.cardBg} ${theme.border}`}>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                        <TrendingUp className="w-6 h-6 text-purple-400" />
                      </div>
                      <div>
                        <p className={`text-2xl font-bold ${theme.textPrimary}`}>{formatCurrency(summary.summary.net_amount)}</p>
                        <p className={`text-xs ${theme.textSecondary}`}>Net Revenue</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* GST vs Non-GST */}
              <div className="grid md:grid-cols-2 gap-4">
                <Card className={`${theme.cardBg} ${theme.border}`}>
                  <CardHeader>
                    <CardTitle className={`${theme.textPrimary} text-lg flex items-center gap-2`}>
                      <Badge className="bg-green-500/20 text-green-400">GST</Badge>
                      GST Billing
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className={theme.textSecondary}>Invoices</span>
                        <span className={`font-bold ${theme.textPrimary}`}>{summary.gst_billing.count}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className={theme.textSecondary}>Total Amount</span>
                        <span className={`font-bold ${theme.textPrimary}`}>{formatCurrency(summary.gst_billing.total)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className={theme.textSecondary}>GST Collected</span>
                        <span className={`font-bold text-green-400`}>{formatCurrency(summary.gst_billing.gst_collected)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className={`${theme.cardBg} ${theme.border}`}>
                  <CardHeader>
                    <CardTitle className={`${theme.textPrimary} text-lg flex items-center gap-2`}>
                      <Badge className="bg-blue-500/20 text-blue-400">Non-GST</Badge>
                      Non-GST Billing
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className={theme.textSecondary}>Invoices</span>
                        <span className={`font-bold ${theme.textPrimary}`}>{summary.non_gst_billing.count}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className={theme.textSecondary}>Total Amount</span>
                        <span className={`font-bold ${theme.textPrimary}`}>{formatCurrency(summary.non_gst_billing.total)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Payment Status & Category Breakdown */}
              <div className="grid md:grid-cols-2 gap-4">
                <Card className={`${theme.cardBg} ${theme.border}`}>
                  <CardHeader>
                    <CardTitle className={`${theme.textPrimary} text-lg`}>Payment Status</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-400" />
                          <span className={theme.textSecondary}>Paid ({summary.payment_status.paid_count})</span>
                        </div>
                        <span className={`font-bold text-green-400`}>{formatCurrency(summary.payment_status.paid_amount)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-amber-400" />
                          <span className={theme.textSecondary}>Unpaid ({summary.payment_status.unpaid_count})</span>
                        </div>
                        <span className={`font-bold text-amber-400`}>{formatCurrency(summary.payment_status.unpaid_amount)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className={`${theme.cardBg} ${theme.border}`}>
                  <CardHeader>
                    <CardTitle className={`${theme.textPrimary} text-lg`}>Category Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className={theme.textSecondary}>Parts</span>
                        <span className={`font-bold ${theme.textPrimary}`}>{formatCurrency(summary.category_breakdown.parts)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className={theme.textSecondary}>Labour</span>
                        <span className={`font-bold ${theme.textPrimary}`}>{formatCurrency(summary.category_breakdown.labour)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className={theme.textSecondary}>Body Shop</span>
                        <span className={`font-bold ${theme.textPrimary}`}>{formatCurrency(summary.category_breakdown.body_shop)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Monthly Tab */}
          {activeTab === 'monthly' && monthlyData && (
            <div className="space-y-6">
              {/* Year Totals */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card className={`${theme.cardBg} ${theme.border}`}>
                  <CardContent className="pt-4 text-center">
                    <p className={`text-2xl font-bold ${theme.textPrimary}`}>{monthlyData.year_totals.total_invoices}</p>
                    <p className={`text-xs ${theme.textSecondary}`}>Total Invoices</p>
                  </CardContent>
                </Card>
                <Card className={`${theme.cardBg} ${theme.border}`}>
                  <CardContent className="pt-4 text-center">
                    <p className={`text-2xl font-bold ${theme.textPrimary}`}>{formatCurrency(monthlyData.year_totals.total_amount)}</p>
                    <p className={`text-xs ${theme.textSecondary}`}>Total Revenue</p>
                  </CardContent>
                </Card>
                <Card className={`${theme.cardBg} ${theme.border}`}>
                  <CardContent className="pt-4 text-center">
                    <p className={`text-2xl font-bold text-amber-400`}>{formatCurrency(monthlyData.year_totals.total_gst)}</p>
                    <p className={`text-xs ${theme.textSecondary}`}>GST Collected</p>
                  </CardContent>
                </Card>
                <Card className={`${theme.cardBg} ${theme.border}`}>
                  <CardContent className="pt-4 text-center">
                    <p className={`text-2xl font-bold text-green-400`}>{formatCurrency(monthlyData.year_totals.total_paid)}</p>
                    <p className={`text-xs ${theme.textSecondary}`}>Received</p>
                  </CardContent>
                </Card>
                <Card className={`${theme.cardBg} ${theme.border}`}>
                  <CardContent className="pt-4 text-center">
                    <p className={`text-2xl font-bold text-red-400`}>{formatCurrency(monthlyData.year_totals.total_unpaid)}</p>
                    <p className={`text-xs ${theme.textSecondary}`}>Pending</p>
                  </CardContent>
                </Card>
              </div>

              {/* Monthly Table */}
              <Card className={`${theme.cardBg} ${theme.border}`}>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className={`${theme.pageBg} border-b ${theme.border}`}>
                          <th className={`px-4 py-3 text-left text-sm font-medium ${theme.textSecondary}`}>Month</th>
                          <th className={`px-4 py-3 text-right text-sm font-medium ${theme.textSecondary}`}>Invoices</th>
                          <th className={`px-4 py-3 text-right text-sm font-medium ${theme.textSecondary}`}>Revenue</th>
                          <th className={`px-4 py-3 text-right text-sm font-medium ${theme.textSecondary}`}>GST</th>
                          <th className={`px-4 py-3 text-right text-sm font-medium ${theme.textSecondary}`}>Received</th>
                          <th className={`px-4 py-3 text-right text-sm font-medium ${theme.textSecondary}`}>Pending</th>
                        </tr>
                      </thead>
                      <tbody>
                        {monthlyData.monthly_data.map((month) => (
                          <tr key={month.month} className={`border-b ${theme.border} hover:${theme.hoverBg}`}>
                            <td className={`px-4 py-3 ${theme.textPrimary} font-medium`}>{month.month_name}</td>
                            <td className={`px-4 py-3 text-right ${theme.textPrimary}`}>{month.invoice_count}</td>
                            <td className={`px-4 py-3 text-right ${theme.textPrimary}`}>{formatCurrency(month.total_amount)}</td>
                            <td className={`px-4 py-3 text-right text-amber-400`}>{formatCurrency(month.gst_amount)}</td>
                            <td className={`px-4 py-3 text-right text-green-400`}>{formatCurrency(month.paid_amount)}</td>
                            <td className={`px-4 py-3 text-right text-red-400`}>{formatCurrency(month.unpaid_amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Invoices Tab */}
          {activeTab === 'invoices' && (
            <Card className={`${theme.cardBg} ${theme.border}`}>
              <CardHeader>
                <CardTitle className={`${theme.textPrimary} flex items-center justify-between`}>
                  <span className="flex items-center gap-2">
                    <Receipt className="w-5 h-5 text-orange-400" />
                    Invoices ({invoices.length})
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className={`${theme.pageBg} border-b ${theme.border}`}>
                        <th className={`px-4 py-3 text-left text-sm font-medium ${theme.textSecondary}`}>Invoice #</th>
                        <th className={`px-4 py-3 text-left text-sm font-medium ${theme.textSecondary}`}>Date</th>
                        <th className={`px-4 py-3 text-left text-sm font-medium ${theme.textSecondary}`}>Customer</th>
                        <th className={`px-4 py-3 text-left text-sm font-medium ${theme.textSecondary}`}>Vehicle</th>
                        <th className={`px-4 py-3 text-right text-sm font-medium ${theme.textSecondary}`}>Amount</th>
                        <th className={`px-4 py-3 text-right text-sm font-medium ${theme.textSecondary}`}>GST</th>
                        <th className={`px-4 py-3 text-center text-sm font-medium ${theme.textSecondary}`}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((inv) => (
                        <tr key={inv.id} className={`border-b ${theme.border} hover:${theme.hoverBg}`}>
                          <td className={`px-4 py-3 ${theme.textPrimary} font-medium`}>{inv.invoice_number}</td>
                          <td className={`px-4 py-3 ${theme.textSecondary}`}>{formatDate(inv.created_at)}</td>
                          <td className={`px-4 py-3 ${theme.textPrimary}`}>{inv.customer_name}</td>
                          <td className={`px-4 py-3 ${theme.textSecondary}`}>{inv.vehicle_number}</td>
                          <td className={`px-4 py-3 text-right ${theme.textPrimary}`}>{formatCurrency(inv.total_amount)}</td>
                          <td className={`px-4 py-3 text-right text-amber-400`}>{formatCurrency(inv.gst_amount)}</td>
                          <td className="px-4 py-3 text-center">
                            {inv.payment_received ? (
                              <Badge className="bg-green-500/20 text-green-400">Paid</Badge>
                            ) : (
                              <Badge className="bg-amber-500/20 text-amber-400">Unpaid</Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {invoices.length === 0 && (
                  <div className={`text-center py-8 ${theme.textSecondary}`}>
                    No invoices found for selected date range
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default Accounting;
